import React, { useState, useEffect } from 'react';
import { X, Save, AlertTriangle } from 'lucide-react';
import { inventoryService } from '../../../services/inventoryService';

const CATEGORIES = [
  { value: 'barrique', label: 'Barrique' },
  { value: 'carton', label: 'Carton' },
  { value: 'accessoire', label: 'Accessoire' },
  { value: 'autre', label: 'Autre' },
];

const inputCls = "w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-400";
const labelCls = "block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1";

const defaultForm = {
  name: '',
  category: 'carton',
  cartonType: '',
  sku: '',
  dimensions: '',
  description: '',
  imageUrl: '',
  purchasePrice: 0,
  salePrice: 0,
  stockQuantity: 0,
  minStock: 10,
  reorderThreshold: 10,
  location: 'Entrepôt principal',
  supplierId: '',
  status: 'active',
};

export default function ItemFormModal({ item, onClose, onSaved }) {
  const [form, setForm] = useState(defaultForm);
  const [suppliers, setSuppliers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadSuppliers();
    if (item) {
      setForm({
        name: item?.name || item?.barrelType || item?.cartonType || '',
        category: item?.category || (item?.tableSource === 'barrel_inventory' ? 'barrique' : 'carton'),
        cartonType: item?.cartonType || item?.barrelType || '',
        sku: item?.sku || '',
        dimensions: item?.dimensions || '',
        description: item?.description || '',
        imageUrl: item?.imageUrl || '',
        purchasePrice: item?.purchasePrice || item?.unitPriceBuy || 0,
        salePrice: item?.salePrice || item?.priceEuros || item?.unitPriceSell || 0,
        stockQuantity: item?.stockQuantity || 0,
        minStock: item?.minStock || item?.minStockLevel || 10,
        reorderThreshold: item?.reorderThreshold || 10,
        location: item?.location || 'Entrepôt principal',
        supplierId: item?.supplierId || '',
        status: item?.status || 'active',
      });
    }
  }, [item]);

  const loadSuppliers = async () => {
    try {
      const data = await inventoryService?.getSuppliers();
      setSuppliers(data);
    } catch (e) {
      console.error(e);
    }
  };

  const handleSave = async () => {
    if (!form?.name?.trim()) {
      setError('Le nom de l\'article est obligatoire');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (item) {
        // Edit existing
        if (item?.tableSource === 'barrel_inventory') {
          await inventoryService?.updateBarrelInventoryItem(item?.id, form);
        } else {
          await inventoryService?.updateCartonInventoryItem(item?.id, form);
        }
      } else {
        // Create new (always carton_inventory for new items)
        await inventoryService?.createCartonInventoryItem(form);
      }
      onSaved?.();
      onClose?.();
    } catch (e) {
      setError(e?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const set = (field, val) => setForm(f => ({ ...f, [field]: val }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-slate-700">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">
            {item ? 'Modifier l\'article' : 'Nouvel article'}
          </h2>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" /> {error}
            </div>
          )}

          {/* Informations générales */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 pb-1 border-b border-gray-200 dark:border-slate-700">
              Informations générales
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Nom article *</label>
                <input className={inputCls} value={form?.name} onChange={e => set('name', e?.target?.value)} placeholder="Ex: Barrique Noire 270L" />
              </div>
              <div>
                <label className={labelCls}>Catégorie</label>
                <select className={inputCls} value={form?.category} onChange={e => set('category', e?.target?.value)}>
                  {CATEGORIES?.map(c => <option key={c?.value} value={c?.value}>{c?.label}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>SKU / Code interne</label>
                <input className={inputCls} value={form?.sku} onChange={e => set('sku', e?.target?.value)} placeholder="Ex: BAR-270-NOIR" />
              </div>
              <div>
                <label className={labelCls}>Type</label>
                <input className={inputCls} value={form?.cartonType} onChange={e => set('cartonType', e?.target?.value)} placeholder="Ex: noir_270l" />
              </div>
              <div>
                <label className={labelCls}>Dimensions</label>
                <input className={inputCls} value={form?.dimensions} onChange={e => set('dimensions', e?.target?.value)} placeholder="Ex: 60x40x40 cm" />
              </div>
              <div>
                <label className={labelCls}>Volume / Capacité</label>
                <input className={inputCls} value={form?.description} onChange={e => set('description', e?.target?.value)} placeholder="Ex: 270 litres" />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>URL Image</label>
                <input className={inputCls} value={form?.imageUrl} onChange={e => set('imageUrl', e?.target?.value)} placeholder="https://..." />
              </div>
            </div>
          </div>

          {/* Prix */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 pb-1 border-b border-gray-200 dark:border-slate-700">
              Prix
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Prix achat (€)</label>
                <input type="number" min="0" step="0.01" className={inputCls} value={form?.purchasePrice} onChange={e => set('purchasePrice', parseFloat(e?.target?.value) || 0)} />
              </div>
              <div>
                <label className={labelCls}>Prix vente (€)</label>
                <input type="number" min="0" step="0.01" className={inputCls} value={form?.salePrice} onChange={e => set('salePrice', parseFloat(e?.target?.value) || 0)} />
              </div>
            </div>
          </div>

          {/* Stock */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 pb-1 border-b border-gray-200 dark:border-slate-700">
              Stock
            </h3>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className={labelCls}>Stock actuel</label>
                <input type="number" min="0" className={inputCls} value={form?.stockQuantity} onChange={e => set('stockQuantity', parseInt(e?.target?.value) || 0)} />
              </div>
              <div>
                <label className={labelCls}>Stock minimum</label>
                <input type="number" min="0" className={inputCls} value={form?.minStock} onChange={e => set('minStock', parseInt(e?.target?.value) || 0)} />
              </div>
              <div>
                <label className={labelCls}>Seuil réappro</label>
                <input type="number" min="0" className={inputCls} value={form?.reorderThreshold} onChange={e => set('reorderThreshold', parseInt(e?.target?.value) || 0)} />
              </div>
            </div>
          </div>

          {/* Logistique */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 pb-1 border-b border-gray-200 dark:border-slate-700">
              Logistique
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Emplacement</label>
                <input className={inputCls} value={form?.location} onChange={e => set('location', e?.target?.value)} placeholder="Entrepôt principal" />
              </div>
              <div>
                <label className={labelCls}>Fournisseur lié</label>
                <select className={inputCls} value={form?.supplierId} onChange={e => set('supplierId', e?.target?.value)}>
                  <option value="">— Aucun fournisseur —</option>
                  {suppliers?.map(s => (
                    <option key={s?.id} value={s?.id}>{s?.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Statut</label>
                <select className={inputCls} value={form?.status} onChange={e => set('status', e?.target?.value)}>
                  <option value="active">Actif</option>
                  <option value="inactive">Inactif</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-slate-700">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700">
            Annuler
          </button>
          <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium disabled:opacity-50">
            <Save className="h-4 w-4" /> {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}
